"use client";

import { cn } from "@/src/lib/utils";
import { SummarizeBarPill } from "./SummarizeBarPill";
import { useEdgeVisibility } from "./useEdgeVisibility";
import { useColumnCenterX } from "./useColumnCenterX";

interface SummarizeBarProps {
  url: string;
  startSentinelId: string;
  endSentinelId: string;
  columnId: string;
}

// Fixed, not sticky/in-flow: pins to the viewport bottom while visible, and
// — because `position: fixed` is fully removed from document flow — never
// reserves any layout space regardless of visibility state or where it sits
// in the DOM. That's deliberate: sticky/in-flow elements keep their box even
// at opacity 0, which previously left a gap in the article. Its JSX position
// in page.tsx no longer matters for layout, so it lives at the top of the
// content column for simplicity.
//
// Horizontal position is measured (via `columnId`) rather than a plain
// `left-1/2`, because `position: fixed` is always viewport-relative — a
// static 50% would center it under the whole page, not under the narrower,
// right-offset content column next to the TOC.
export function SummarizeBar({ url, startSentinelId, endSentinelId, columnId }: SummarizeBarProps) {
  const visible = useEdgeVisibility(startSentinelId, endSentinelId);
  const centerX = useColumnCenterX(columnId);

  return (
    <div
      className={cn(
        "fixed bottom-6 z-30 -translate-x-1/2 transition-all duration-500 ease-out",
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0",
      )}
      style={{ left: centerX !== null ? `${centerX}px` : "50%" }}
      aria-hidden={!visible}
    >
      <SummarizeBarPill url={url} />
    </div>
  );
}
